import { Icon, mq, Space, Stack, Text } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Checkbox } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { formatNullableCurrency } from "../../utils/formatNullableCurrency";
import { useCanViewClientPortalPricing } from "../permissions/Permissions";

interface Props {
  bundleName: string;
  checks: any;
  price: string;
  isSelected?: boolean;
  onPress: () => void;
  onRemove?: () => void;
  showRemoveWhenSelected?: boolean;
  testID: string;
  currency: string;
}

export function Bundle({
  bundleName,
  checks,
  price,
  isSelected,
  onPress,
  onRemove,
  showRemoveWhenSelected = false,
  testID,
  currency,
}: Props) {
  const { t } = useTranslation();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();

  const CAPACITY = 3;
  const bundlePrice = formatNullableCurrency(price, {
    currency,
    locale: "en-CA",
    fallback: null,
  });

  const handlePress = () => {
    if (onPress) onPress();
  };

  return (
    <Checkbox
      className={styles.bundleCard}
      isSelected={isSelected}
      onChange={handlePress}
    >
      {({ isSelected }) => (
        <div
          data-testid={`${testID}-bundle-card-${
            isSelected ? "selected" : "unselected"
          }`}
        >
          <Stack gap="space-2" className={styles.top}>
            <Text style="global/label-small" className={styles.center}>
              <Icon name="shopping_mode" color="icon-accent-orange" />
              {t("Bundled deal")}
            </Text>
            <Text
              style="global/heading-medium"
              testID={`${testID}-bundle-name`}
            >
              {bundleName}
            </Text>
            <div>
              <Text style="global/info-label" testID={`${testID}-bundle-check`}>
                {checks.slice(0, CAPACITY).join(", ")}
              </Text>
              {checks.length > CAPACITY && (
                <Text
                  style="global/info-label"
                  testID={`${testID}-bundle-check-overflow`}
                >
                  {` +${checks.length - CAPACITY}`}
                </Text>
              )}
            </div>
          </Stack>
          <Space />
          <Stack orientation="horizontal" className={styles.top}>
            <Text
              style="global/heading-small"
              color="text-accent-orange-strong"
              testID={`${testID}-bundle-price`}
            >
              {canViewClientPortalPricing && bundlePrice
                ? t("From {{bundlePrice}}", { bundlePrice })
                : ""}
            </Text>
            <Space />
            {isSelected && showRemoveWhenSelected && onRemove ? (
              <button
                type="button"
                className={styles.removeButton}
                onClick={(event) => {
                  event.stopPropagation();
                  onRemove();
                }}
                aria-label={t("Remove bundled deal")}
                data-testid={`${testID}-bundle-remove-button`}
              >
                <Text
                  style="global/label-small"
                  className={styles.center}
                  color="text-accent-red-strong"
                >
                  <Icon name="delete" />
                  {t("Remove")}
                </Text>
              </button>
            ) : (
              isSelected && (
                <Text
                  style="global/label-small"
                  className={styles.center}
                  testID={`${testID}-bundled-selected`}
                >
                  <Icon name="check" />
                  {t("Added")}
                </Text>
              )
            )}
          </Stack>
        </div>
      )}
    </Checkbox>
  );
}

const styles = {
  bundleCard: css(
    {
      display: "flex",
      flexDirection: "column",
      minHeight: 160,
      minWidth: 240,
      borderRadius: "var(--border-radius-3)",
      backgroundColor: "var(--background-accent-orange-weakest)",
      "&:hover": {
        boxShadow: "var(--shadow-card)",
      },
      "&[data-focus-visible]": {
        boxShadow: "var(--focus-action)",
      },
      cursor: "pointer",
      flex: 1,
    },
    mq({
      maxWidth: ["none", "none", 314.67],
    })
  ),
  top: css({
    padding: `var(--space-3) var(--space-3) var(--space-0)`,
    paddingBottom: "var(--space-4)",
  }),
  center: css({
    display: "flex",
    alignItems: "center",
    gap: "var(--space-1)",
  }),
  removeButton: css({
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
  }),
};
